import { AttacherNode, AttachmentSubsolution } from "@animech/supermodel";
import { DeepReadonly } from "@animech/axiom-format";
import { AttacherIdentifier } from "./attacher-attribute-map";
import { objectKeys } from "src/utility";
import { Model } from "@definitions";

type GroupSubsolutionValidator = (
	subsolution: AttachmentSubsolution,
) => boolean;

export interface AttacherGroupDefinition {
	modelIds?: Model[];
	excludedModelIds?: Model[];
	variants: [AttacherGroupVariant, ...AttacherGroupVariant[]];
}

export type AttacherGroupVariant = {
	attachers: AttacherIdentifier[];
};

export interface AttacherGroup {
	readonly definition: DeepReadonly<AttacherGroupDefinition>;
	readonly supportedModelIds: ReadonlySet<Model>;
	validateSubsolution: GroupSubsolutionValidator;
}

export function createAttacherGroup(
	definition: AttacherGroupDefinition,
	modelNodesMap: Map<Model, AttacherNode[]>,
	nodeModelMap: Map<AttacherNode, Model>,
): AttacherGroup {
	const variantValidators: GroupSubsolutionValidator[] = [];
	const supportedModelIds = new Set<Model>();
	const possibleModelNodesMap = new Map(
		[...modelNodesMap.entries()].filter(
			([modelId]) =>
				!definition.excludedModelIds?.includes(modelId) &&
				definition.modelIds?.includes(modelId) !== false,
		),
	);

	for (const variant of definition.variants) {
		const variantModelNodesMap = new Map<Model, Set<AttacherNode>>();

		for (const [modelId, modelNodes] of possibleModelNodesMap) {
			const variantNodes = new Set<AttacherNode>();
			const unmatchedDescriptions = new Set(variant.attachers);

			for (const modelNode of modelNodes) {
				const matchingDescriptions = getAttacherIdentifiersMatchingNode(
					variant.attachers,
					modelNode,
				);
				for (const description of matchingDescriptions) {
					variantNodes.add(modelNode);
					unmatchedDescriptions.delete(description);
				}
			}
			if (unmatchedDescriptions.size === 0) {
				supportedModelIds.add(modelId);
				variantModelNodesMap.set(modelId, variantNodes);
			} else if (definition.modelIds?.includes(modelId)) {
				throw new Error(
					`Failed to match included model ${modelId} to the group: ${JSON.stringify(definition)}`,
				);
			}
		}

		if (variantModelNodesMap.size === 0) {
			throw new Error(
				`No matching models found for the group variant: ${JSON.stringify(variant)}`,
			);
		}

		const variantValidator: GroupSubsolutionValidator = (
			subsolution: AttachmentSubsolution,
		) => {
			for (const entityTransform of subsolution.entityTransforms) {
				const modelId =
					entityTransform.instances.length > 0 &&
					nodeModelMap.get(entityTransform.instances[0].node);
				const nodes = modelId && variantModelNodesMap.get(modelId);
				if (!nodes) {
					// This *should* never happen under normal circumstances
					// since nodeModelMap should only contain entries for nodes
					// that are present in modelGroupNodesMap for this group.
					return true;
				}
				for (const attacher of entityTransform.instances) {
					if (nodes.has(attacher.node) && attacher.attachments.length === 0) {
						return false;
					}
				}
			}
			return true;
		};

		variantValidators.push(variantValidator);
	}

	return {
		definition,
		supportedModelIds,
		validateSubsolution: (entityTransform) => {
			for (const validateVariant of variantValidators) {
				if (validateVariant(entityTransform)) {
					return true;
				}
			}
			return false;
		},
	};
}

function getAttacherIdentifiersMatchingNode(
	attacherDescriptions: AttacherIdentifier[],
	node: AttacherNode,
): Set<AttacherIdentifier> {
	const matchingDescriptions = new Set<AttacherIdentifier>();

	outer: for (const description of attacherDescriptions) {
		if (matchingDescriptions.has(description)) {
			continue;
		}
		const propertyKeys = objectKeys(description);
		for (const propertyKey of propertyKeys) {
			if (node[propertyKey] !== description[propertyKey]) {
				continue outer;
			}
		}
		matchingDescriptions.add(description);
	}

	return matchingDescriptions;
}
